"use client";

import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

interface DropdownFiltersProps {
  subjectOptions: string[];
  typeOptions: string[];
  landelijkOptions: string[];
}

const websiteId = (process.env.NEXT_PUBLIC_WEBSITE_ID as "nsc" | "jsc") || "nsc";

export const DropdownFilters = ({ subjectOptions, typeOptions, landelijkOptions }: DropdownFiltersProps) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const handleFilterChange = (filterName: string, value: string) => {
    const current = new URLSearchParams(Array.from(searchParams.entries()));

    if (value && value !== "all") {
      current.set(filterName, value);
    } else {
      current.delete(filterName);
    }
    // Remove page when filters change to reset to first page
    current.delete("page");

    const query = current.toString();
    router.push(`${pathname}${query ? `?${query}` : ""}`, { scroll: false });
  };

  const handleClearFilters = () => {
    const current = new URLSearchParams(Array.from(searchParams.entries()));
    current.delete("subject");
    current.delete("type");
    current.delete("landelijk");
    current.delete("page"); // Also reset page
    const query = current.toString();
    router.push(`${pathname}${query ? `?${query}` : ""}`, { scroll: false });
  };

  const getSelectedValue = (filterName: string) => {
    return searchParams.get(filterName) || "all";
  };

  const filterConfig = [
    {
      name: "subject",
      label: "Onderwerp",
      options: ["all", ...subjectOptions],
      selectedValue: getSelectedValue("subject"),
    },
    {
      name: "type",
      label: "Type",
      options: ["all", ...typeOptions],
      selectedValue: getSelectedValue("type"),
    },
    {
      name: "landelijk",
      label: "Landelijk/Regionaal",
      options: ["all", ...landelijkOptions],
      selectedValue: getSelectedValue("landelijk"),
    },
  ];

  const isActiveFilterSelected = [
    searchParams.get("subject"),
    searchParams.get("type"),
    searchParams.get("landelijk"),
  ].some((value) => value && value !== "all");

  return (
    <div className="mb-8 flex flex-col gap-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
        {filterConfig.map((filter) => (
          <div key={filter.name} className="flex flex-col gap-1.5">
            <Label htmlFor={filter.name} className="text-sm font-medium">
              {filter.label}
            </Label>
            <Select
              name={filter.name}
              value={filter.selectedValue}
              onValueChange={(value) => handleFilterChange(filter.name, value)}
            >
              <SelectTrigger
                className={cn(
                  "w-full rounded border shadow-sm",
                  websiteId === "jsc" ? "border-jsc-sky-blue" : "border-nsc-dark-blue"
                )}
              >
                <SelectValue placeholder={`Selecteer een ${filter.label.toLowerCase()}`} />
              </SelectTrigger>
              <SelectContent>
                {filter.options.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option === "all" ? `Alle` : option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>
      {isActiveFilterSelected && (
        <Button variant={"secondary"} onClick={handleClearFilters} className="w-full md:w-auto md:self-start">
          Alle filters wissen
        </Button>
      )}
    </div>
  );
};

export default DropdownFilters;
